import { useCallback, useMemo } from 'react'
import { useAccount } from '../contexts/AccountContext'
import { Link, useNavigate } from '@tanstack/react-router'
import { useGQL_teams, useMe } from '../contexts/graphql-and-swr-apis'
import { clsx } from 'clsx'
import { faSignOut, faUser, faUsers } from '@fortawesome/pro-light-svg-icons'
import { FunDropdownMenu, type FunMenuItem } from '@unwrittenmedia/simple-components'

interface HeaderLoginButtonProps {
  className?: string
  forceDark?: boolean
}

const styles = {
  userMenu: "relative inline-block text-left",
  guestWrapper: "flex items-center space-x-4",
  learnMoreLink: "hover:text-purple-600 font-medium py-2",
  hostLoginLink: "bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors",
}

function UserMenu({ className, forceDark }: { className?: string; forceDark?: boolean }) {
  const { state, dispatch } = useAccount()
  const navigate = useNavigate()
  const { data: teamsData } = useGQL_teams()
  const { me } = useMe()

  const menuItems: FunMenuItem[] = useMemo(() => {
    const teamItems: FunMenuItem[] = teamsData?.teams?.map(team => ({
      id: `team:${team.id}`,
      label: team.name,
      icon: faUsers,
    })) ?? []

    const userDisplayName = me?.name || me?.email || 'My Profile'

    return [
      { id: 'profile', label: userDisplayName, icon: faUser },
      { id: 'separator1', label: '', isBreak: true },
      ...teamItems,
      { id: 'separator2', label: '', isBreak: true },
      { id: 'logout', label: 'Sign out', icon: faSignOut, variant: 'danger' as const },
    ]
  }, [teamsData, me])

  const handleAction = useCallback((action: string) => {
    if (action === 'logout') {
      dispatch({ type: 'LOGOUT' })
      globalThis.location.assign('/api/logout')
    } else if (action === 'profile') {
      navigate({ to: '/portal/profile' })
    } else if (action.startsWith('team:')) {
      const teamId = action.replace('team:', '')
      navigate({ to: '/team/$teamId/manage', params: { teamId } })
    }
  }, [dispatch, navigate])

  return (
    <div className={clsx(styles.userMenu, forceDark ? "[&_svg]:text-mist-300" : "dark:[&_svg]:text-mist-300", className)}>
      <FunDropdownMenu
        items={menuItems}
        onAction={handleAction}
        user={state}
        triggerColor="white"
        triggerIcon={faUser}
      />
    </div>
  )
}

export default function HeaderLoginButton({ className, forceDark = false }: HeaderLoginButtonProps) {
  const { state } = useAccount()

  if (state.isAuthenticated) {
    return <UserMenu className={className} forceDark={forceDark} />
  }

  return (
    <div className={clsx(styles.guestWrapper, className)}>
      {/* TODO: /attendee page that talks about the product and has a "join" button that opens the join modal. For now, just link to home page. */}
      <Link to="/" className={clsx(styles.learnMoreLink, forceDark ? "text-stone-400" : "text-stone-600 dark:text-stone-400")}>
        Learn More
      </Link>
      <Link to="/login" className={styles.hostLoginLink}>
        Host Log in
      </Link>
    </div>
  )
}
